import * as GAME from 'vibegame';
import { completeSequence, playSequence, resetSequence } from 'vibegame/tweening';

export const SequenceController = GAME.defineComponent({
  step: GAME.Types.i32,
  targetStep: GAME.Types.i32,
  maxStep: GAME.Types.i32,
});

type SequenceMap = Record<string, string>;

function calculateMaxStep(sequenceMap: SequenceMap): number {
  let maxStep = 0;
  for (const key of Object.keys(sequenceMap)) {
    const parts = key.split('-');
    if (parts.length === 2) {
      const from = parseInt(parts[0], 10);
      const to = parseInt(parts[1], 10);
      if (!isNaN(from) && !isNaN(to)) {
        maxStep = Math.max(maxStep, from, to);
      }
    }
  }
  return maxStep;
}

export function createSequenceControllerPlugin(sequenceMap: SequenceMap): GAME.Plugin {
  const autoDetectedMaxStep = calculateMaxStep(sequenceMap);

  function completeAllSequences(state: GAME.State): void {
    for (const seqName of Object.values(sequenceMap)) {
      const seq = state.getEntityByName(seqName);
      if (seq !== null) {
        completeSequence(state, seq);
      }
    }
  }

  const sequenceControllerQuery = GAME.defineQuery([SequenceController]);

  const SequenceControllerInitSystem: GAME.System = {
    group: 'setup',
    update(state) {
      for (const eid of sequenceControllerQuery(state.world)) {
        if (SequenceController.maxStep[eid] === 0) {
          SequenceController.maxStep[eid] = autoDetectedMaxStep;
        }
      }
    },
  };

  const SequenceControllerSystem: GAME.System = {
    group: 'simulation',
    update(state) {
      for (const eid of sequenceControllerQuery(state.world)) {
        const target = SequenceController.targetStep[eid];
        const current = SequenceController.step[eid];

        if (current !== target) {
          completeAllSequences(state);

          const seqName = sequenceMap[`${current}-${target}`];
          const seq = seqName ? state.getEntityByName(seqName) : null;

          if (seq !== null) {
            resetSequence(state, seq);
            playSequence(state, seq);
          }

          SequenceController.step[eid] = target;
        }
      }
    },
  };

  return {
    components: { SequenceController },
    systems: [SequenceControllerInitSystem, SequenceControllerSystem],
    config: {
      defaults: {
        'sequence-controller': { step: 0, targetStep: 0, maxStep: 0 },
      },
    },
  };
}

export function createSequenceStepperUI(
  state: GAME.State,
  controllerName: string,
  prevButtonId: string,
  nextButtonId: string
): void {
  const controller = state.getEntityByName(controllerName);
  if (controller === null) return;

  const btnPrev: HTMLButtonElement | null = document.getElementById(
    prevButtonId
  ) as HTMLButtonElement | null;
  const btnNext: HTMLButtonElement | null = document.getElementById(
    nextButtonId
  ) as HTMLButtonElement | null;

  const updateButtons = () => {
    if (!btnPrev || !btnNext) return;
    const current = SequenceController.targetStep[controller];
    const max = SequenceController.maxStep[controller];
    btnPrev.disabled = current <= 0;
    btnNext.disabled = current >= max;
  };

  btnPrev?.addEventListener('click', () => {
    const current = SequenceController.targetStep[controller];
    if (current > 0) {
      SequenceController.targetStep[controller] = current - 1;
    }
  });

  btnNext?.addEventListener('click', () => {
    const current = SequenceController.targetStep[controller];
    const max = SequenceController.maxStep[controller];
    if (current < max) {
      SequenceController.targetStep[controller] = current + 1;
    }
  });

  const StepUISystem: GAME.System = {
    group: 'simulation',
    update: updateButtons,
  };

  state.registerSystem(StepUISystem);
  updateButtons();
}
